import React, { createContext, useContext, useEffect, useState } from 'react'

export type SidebarTheme = 'original' | 'dark' | 'white'
export type SidebarColor = 'pink' | 'gray' | 'blue' | 'green' | 'orange' | 'red'

interface SidebarThemeContextType {
  theme: SidebarTheme
  setTheme: (theme: SidebarTheme) => void
  color: SidebarColor
  setColor: (color: SidebarColor) => void
}

const SidebarThemeContext = createContext<SidebarThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'sidebar-theme'
const COLOR_STORAGE_KEY = 'sidebar-color'

// Theme configurations - matches CD
const themeConfig = {
  original: {
    background: '142 70% 35%',
    foreground: '142 100% 94%',
    primary: '142 70% 28%',
    primaryForeground: '0 0% 100%',
    accent: '142 70% 40%',
    accentForeground: '142 100% 94%',
    border: '142 75% 30%',
    ring: '142 75% 50%',
    rightBorder: '142 70% 35%',
  },
  dark: {
    background: '0 0% 15%',
    foreground: '0 0% 90%',
    primary: '217 91% 60%',
    primaryForeground: '0 0% 100%',
    accent: '0 0% 37%',
    accentForeground: '0 0% 90%',
    border: '0 0% 10%',
    ring: '217 91% 60%',
    rightBorder: '0 0% 15%',
  },
  white: {
    background: '0 0% 100%',
    foreground: '220 13% 28%',
    primary: '217 91% 60%',
    primaryForeground: '0 0% 100%',
    accent: '220 13% 96%',
    accentForeground: '220 13% 28%',
    border: '220 13% 91%',
    ring: '217 91% 60%',
    rightBorder: '220 13% 91%',
  },
}

// Color gradient configurations
const colorConfig = {
  pink: { from: '#ec4899', to: '#f472b6' },
  gray: { from: '#374151', to: '#6b7280' },
  blue: { from: '#16a34a', to: '#4ade80' },
  green: { from: '#22c55e', to: '#4ade80' },
  orange: { from: '#f97316', to: '#fb923c' },
  red: { from: '#ef4444', to: '#f87171' },
}

interface SidebarThemeProviderProps {
  children: React.ReactNode
}

export function SidebarThemeProvider({ children }: SidebarThemeProviderProps) {
  const [theme, setThemeState] = useState<SidebarTheme>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored === 'original' || stored === 'dark' || stored === 'white') {
        return stored as SidebarTheme
      }
    } catch {
      // Ignore localStorage errors
    }
    return 'original'
  })

  const [color, setColorState] = useState<SidebarColor>(() => {
    try {
      const stored = localStorage.getItem(COLOR_STORAGE_KEY)
      if (stored === 'pink' || stored === 'gray' || stored === 'blue' || stored === 'green' || stored === 'orange' || stored === 'red') {
        return stored as SidebarColor
      }
    } catch {
      // Ignore localStorage errors
    }
    return 'red'
  })

  // Apply theme CSS variables
  useEffect(() => {
    const config = themeConfig[theme]
    const root = document.documentElement

    root.style.setProperty('--sidebar-background', config.background)
    root.style.setProperty('--sidebar-foreground', config.foreground)
    root.style.setProperty('--sidebar-primary', config.primary)
    root.style.setProperty('--sidebar-primary-foreground', config.primaryForeground)
    root.style.setProperty('--sidebar-accent', config.accent)
    root.style.setProperty('--sidebar-accent-foreground', config.accentForeground)
    root.style.setProperty('--sidebar-border', config.border)
    root.style.setProperty('--sidebar-ring', config.ring)
    root.style.setProperty('--sidebar-right-border', config.rightBorder)
  }, [theme])

  // Apply color CSS variables
  useEffect(() => {
    const colors = colorConfig[color]
    const root = document.documentElement

    root.style.setProperty('--sidebar-gradient-from', colors.from)
    root.style.setProperty('--sidebar-gradient-to', colors.to)
  }, [color])

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // Ignore
    }
  }, [theme])

  useEffect(() => {
    try {
      localStorage.setItem(COLOR_STORAGE_KEY, color)
    } catch {
      // Ignore
    }
  }, [color])

  return (
    <SidebarThemeContext.Provider value={{ theme, setTheme: setThemeState, color, setColor: setColorState }}>
      {children}
    </SidebarThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSidebarTheme(): SidebarThemeContextType {
  const context = useContext(SidebarThemeContext)
  if (context === undefined) {
    throw new Error('useSidebarTheme must be used within a SidebarThemeProvider')
  }
  return context
}
